// Presents 480x270 scene buffers on the full-window screen canvas.
// Landscape: the scene covers the screen, cropping up to CROP of its width or CROP_ROWS of its height (the sides
// centred, a third of the rows off the sky and two off the ground) but never into its frame (see frameOf), the tip of
// its landmark (landmarkTop: the sky only goes down to it, the rest comes off the ground) or a shown billboard. 21:9
// fills, even in a browser window. A screen wider or taller than that (32:9, 4:3) shows the rest as a dimmed,
// stretched copy of the scene ("ambient" bars).
// Portrait: the scene's frame (its landmark and its billboard, see frameOf; the landmark alone once the visitor hid the
// billboards, frameBillboard) always shows. When the frame fits a screen-tall crop, the scene fills the screen, cropped
// around focusX as far as the frame allows. When it doesn't, the view zooms out to the frame's width (a "band") and the
// scene's sky and ground carry on above and below it.
// A drag pans a portrait view sideways (pan); each city keeps its offset until recentre() or the phone turns.
// A view is { scene, buf }: a scene module and the buffer it renders into.
// Overlays (the Together lanterns and reactions) go on top of a view with its layout, pan included, after the scene.
// So do sprites (the sale plane), which cross the scene in steps of the screen's own pixels, not the scene's.
// The render loop asks due(now, fps) before it presents (createPacer): a 120 Hz screen gets every other frame, the 60 a
// second a 60 Hz screen shows, instead of twice the work, and a visitor who picked less motion (motionFps) fewer still
// (a finger dragging the view always gets the scenes' own 60).
// present() says whether the frame reached the screen: not while the browser has lost its graphics context (onLost,
// onRestored). createHealth keeps count of that for the app, which says so on the page when the scene stays missing.
import { W, H, FPS, clamp, bayer, makeCanvas } from './core.js';

const PAD = 3;                   // scene px kept clear on each side of a frame
const CROP = 0.2;                // the most of the scene's width a landscape screen crops off to fill itself
const CROP_ROWS = 0.35;          // and of its height: screens up to 2.73:1 fill (a 3440x1440 monitor under a browser's tabs)
const TOP = 1 / 3;               // the share of a landscape screen's vertical crop taken off the sky (the rest: the ground)
const SHADE = [0.45, 0.65];      // how much darker the sky above / the ground below a band gets by the screen edge
const LEVELS = 3;                // dither steps of that darkening

/**
 * Paces a requestAnimationFrame loop in whole display frames, so motion stays even: due(now) says yes to every nth
 * frame, n the most that still make `fps` a second (fps 60: every frame at 60 Hz, every other one at 120 Hz and
 * 144 Hz, every fourth at 240; 90 Hz keeps all 90, as every other one would be 45). The display's frame length is
 * measured every half second: the mean gap between rAF times, leaving out gaps half again as long as the median (a
 * dropped frame), so neither jitter nor dropped frames move it. Until it's known, and on the first frame after a pause
 * (a hidden tab), every frame is due. due must see every rAF time `now`; a yes books the slot. A call can ask for
 * another rate than the pacer's own (due(now, rate)): a higher one than the call before draws as soon as its own
 * gap has passed, without waiting out the slower one's.
 */
export function createPacer(fps) {
  let prev = -Infinity, since = 0, period = 0, count = 0;
  const gaps = [];                                    // this half second's gaps between rAF times
  return function due(now, rate = fps) {
    const gap = now - prev;
    prev = now;
    if (gap > 250) { gaps.length = 0; since = now; count = 0; return true; }
    gaps.push(gap);
    if (now - since >= 500) {
      const mid = gaps.sort((a, b) => a - b)[gaps.length >> 1], whole = gaps.filter(g => g < mid * 1.5);
      period = whole.reduce((sum, g) => sum + g, 0) / whole.length;
      gaps.length = 0; since = now;
    }
    if (!period) return true;
    count += Math.max(1, Math.floor(gap / period + 0.25));           // display frames since the last rAF: from 1.75, one was dropped
    if (count < Math.floor(1000 / rate / period + 0.1)) return false; // + 0.1: 120 Hz measured a hair slow still halves
    count = 0;
    return true;
  };
}

/**
 * Frames a second for a Motion pick (Settings, site/js/app.js): 'smooth' the scenes' own 60, 'light' a third of them,
 * 'still' one, of a scene whose time stands still. A still gets light's rate while something plays out over it
 * (`busy`: a cross-fade to another city or pick, a tapped spot's answer, a reaction on its way up).
 */
export const MOTION_FPS = { smooth: FPS, light: 20, still: 1 };
export const motionFps = (mode, busy = false) => (mode === 'still' && busy ? MOTION_FPS.light : MOTION_FPS[mode] ?? FPS);

/**
 * Whether the scene is reaching the screen. The render loop says shown() for every frame it put there, and a timer
 * says waited(ms) for the time the page spent drawing meanwhile. Too long without a frame (stallMs; bootMs before the
 * first one, which a slow start may take) and waited answers why, once: what the page blamed since the last frame
 * (blame: 'render', 'context'; known trouble never gets the longer wait), or else 'boot' before the first frame and
 * 'stalled' after it. fail(reason) is the same at once, for a failure the page knows is there to stay. The next frame
 * ends it: that shown() answers the reason, once (null every other time).
 */
export function createHealth({ bootMs = 10000, stallMs = 5000 } = {}) {
  let quiet = 0, seen = false, cause = null, failed = null;
  return {
    get failed() { return failed; },
    blame(reason) { cause = reason; },
    fail(reason) { return (failed = reason); },
    waited(ms) {
      if (failed) return null;
      quiet += ms;
      return quiet < (seen || cause ? stallMs : bootMs) ? null : (failed = cause ?? (seen ? 'stalled' : 'boot'));
    },
    shown() {
      const was = failed;
      quiet = 0; seen = true; cause = failed = null;
      return was;
    },
  };
}

/** the scene span [x0, x1] every screen shows (a landscape crop, a portrait frame): the landmark, and the billboard
 *  next to it (billboard: false, the billboards hidden: the landmark alone) */
export function frameOf(scene, { billboard = true } = {}) {
  const { landmark: [a, b] = [0, W], billboard: bb } = scene ?? {};
  return bb && billboard ? [Math.min(a, bb.x), Math.max(b, bb.x + bb.w)] : [a, b];
}

/**
 * Where a scene goes on a cw x ch screen (device px): the source rect sx/sy/sw/sh in the scene, the destination
 * dx/dy/dw/dh on the screen. pan: scene px a portrait view was dragged by (+ shows more of the right).
 * inset: device px the title covers at the top and the dock at the bottom; a band is centred between them.
 * frame: frameOf's options (billboard: false frames the landmark alone).
 * A portrait layout also returns home, its sx before the pan.
 */
export function layout(cw, ch, scene, pan = 0, inset = { top: 0, bottom: 0 }, frame = {}) {
  if (cw < ch) {
    const [x0, x1] = frameOf(scene, frame), span = x1 - x0;
    const tall = cw * H / ch;                         // the scene width a screen-tall crop shows
    const sw = span <= tall ? tall : Math.min(W, span + 2 * PAD);
    const pad = Math.min(PAD, (sw - span) / 2);
    const home = clamp(Math.round(clamp((scene?.focusX ?? W / 2) - sw / 2, x1 + pad - sw, x0 - pad)), 0, W - sw);
    const sx = clamp(Math.round(home + pan), 0, W - sw);
    const dh = sw === tall ? ch : Math.round(H * cw / sw);
    const dy = clamp(Math.round((inset.top + ch - inset.bottom - dh) / 2), 0, ch - dh);
    return { sx, sy: 0, sw, sh: H, dx: 0, dy, dw: cw, dh, bars: false, band: dh < ch, home };
  }
  // Landscape: cover the screen, cropping at most CROP of the scene's width and CROP_ROWS of its height, never into
  // its frame or the rows [y0, y1] from the landmark's tip (no landmarkTop: the whole sky) to a shown billboard's foot;
  // past that, the rest is ambient bars. A scale just under a whole number rounds up to it (crisper) when that crops no more.
  const [x0, x1] = frameOf(scene, frame), bb = frame.billboard !== false ? scene?.billboard : null;
  const tip = scene?.landmarkTop ?? 0, [y0, y1] = bb ? [Math.min(tip, bb.y), bb.y + bb.h] : [tip, tip];
  const most = Math.min(cw / Math.max(W * (1 - CROP), Math.min(W, x1 - x0 + 2 * PAD)),
    ch / Math.max(H * (1 - CROP_ROWS), Math.min(H, y1 - y0 + 2 * PAD)));
  let scale = Math.min(Math.max(cw / W, ch / H), most);
  const whole = Math.ceil(scale - 1e-9);
  if (whole >= 2 && whole - scale < 0.12 && whole <= most) scale = whole;
  const sw = Math.min(W, cw / scale), sh = Math.min(H, ch / scale);
  // whole pixels: round the free spot, then keep to the bounds rounded inwards (rounding a spot on a bound could cut
  // the frame's edge by a fraction of a pixel)
  const sx = clamp(clamp(Math.round((W - sw) / 2), Math.ceil(x1 + PAD - sw - 1e-9), Math.floor(x0 - PAD + 1e-9)), 0, W - sw);
  const sy = clamp(clamp(Math.round((H - sh) * TOP), Math.ceil(y1 + PAD - sh - 1e-9), Math.floor(y0 - PAD + 1e-9)), 0, H - sh);
  const dw = sw < W ? cw : Math.round(W * scale), dh = sh < H ? ch : Math.round(H * scale);
  const dx = Math.round((cw - dw) / 2), dy = Math.round((ch - dh) / 2);
  return { sx, sy, sw, sh, dx, dy, dw, dh, bars: dx > 2 || dy > 2, band: false };
}

/** css rgb() of the average colour of two scene rows, x..x+w from row y */
function average(ctx, x, w, y) {
  const d = ctx.getImageData(x, y, w, 2).data, n = d.length / 4;
  let r = 0, g = 0, b = 0;
  for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
  return `rgb(${Math.round(r / n)},${Math.round(g / n)},${Math.round(b / n)})`;
}

/** W x (up + down) black overlay that darkens a band's surroundings: rows [0, up) above it, [up, up + down) below */
function makeShade(up, down) {
  const [c, x] = makeCanvas(W, up + down), img = x.createImageData(W, up + down), d = img.data;
  for (let y = 0; y < up + down; y++) {
    const above = y < up, u = above ? (up - y) / up : (y - up + 1) / down;   // near 0 by the band, 1 at the screen edge
    const a = SHADE[above ? 0 : 1] / LEVELS * 255;
    for (let i = 0; i < W; i++) {
      const f = u * LEVELS, lvl = Math.min(LEVELS, Math.floor(f) + (f % 1 > bayer(i, y) ? 1 : 0));
      d[(y * W + i) * 4 + 3] = Math.round(lvl * a);
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}

export function createDisplay(canvas, { onLost = () => {}, onRestored = () => {} } = {}) {
  const sctx = canvas.getContext('2d', { alpha: false });
  let cw = 1, ch = 1, inset = { top: 0, bottom: 0 };
  let pans = new WeakMap();                           // view -> scene px it was dragged by
  const shades = new Map();                           // "up,down" -> makeShade canvas, for the band sizes on this screen
  const pace = createPacer(FPS);                      // the scenes' own 60 a second, unless the app asks for fewer (motionFps)
  let cleared = false;                                // resize() blanked the canvas: the next frame draws, due or not
  let panned = false;                                 // pan() moved the view: the next frame comes at the scenes' own rate, whatever was asked
  let frame = { billboard: true };                    // frameOf's options: whether portrait frames keep the billboard
  // The browser can lose its graphics context (its GPU process crashed or was restarted): every <canvas> of the page
  // goes blank, this one included, and takes nothing until the browser has restored it (by itself, unless contextlost
  // is cancelled). Back, it is blank with its settings reset: the next frame draws, due or not, the shades are made
  // again, and the app sees to what its scenes drew ahead, where that was lost too (onRestored).
  let lost = sctx.isContextLost?.() ?? false;
  canvas.addEventListener?.('contextlost', () => { lost = true; onLost(); });
  canvas.addEventListener?.('contextrestored', () => { lost = false; cleared = true; shades.clear(); onRestored(); });

  /** whether the render loop should present at rAF time `now`, at `fps` a second (createPacer; the scenes' own while
   *  a finger drags the view, so it follows), or at once after a resize */
  function due(now, fps = FPS) {
    const yes = pace(now, panned ? FPS : fps) || cleared;
    cleared = panned = false;
    return yes;
  }

  /** fit the canvas to the window; top / bottom: CSS px the title and the dock cover */
  function resize({ top = 0, bottom = 0 } = {}) {
    const dpr = Math.min(window.devicePixelRatio || 1, 3), portrait = cw < ch;
    cw = Math.max(1, Math.round(innerWidth * dpr));
    ch = Math.max(1, Math.round(innerHeight * dpr));
    canvas.width = cw; canvas.height = ch;
    cleared = true;
    canvas.style.width = innerWidth + 'px';
    canvas.style.height = innerHeight + 'px';
    inset = { top: top * dpr, bottom: bottom * dpr };
    shades.clear();
    if ((cw < ch) !== portrait) pans = new WeakMap();   // turning the phone starts every city from its frame again
  }

  const place = v => layout(cw, ch, v.scene, pans.get(v) ?? 0, inset, frame);

  /** whether portrait frames keep the billboard in view (false: the visitor hid the billboards); every city starts
   *  from its new frame, as when the phone turns */
  function frameBillboard(on) {
    if (on === frame.billboard) return;
    frame = { billboard: on };
    pans = new WeakMap();
    cleared = true;
  }

  // A band carries its scene on above and below: the average colour of the scene's top (bottom) two rows, darkened
  // towards the screen edge in dithered, scene-sized pixels. The dither spans the whole scene width, so it pans along.
  function surround(buf, L) {
    const k = L.dw / L.sw, below = L.dy + L.dh;
    const up = Math.ceil(L.dy / k), down = Math.ceil((ch - below) / k), key = `${up},${down}`;
    if (!shades.has(key)) shades.set(key, makeShade(up, down));
    const shade = shades.get(key), src = buf.getContext('2d');
    if (up) {
      sctx.fillStyle = average(src, L.sx, L.sw, 0);
      sctx.fillRect(0, 0, cw, L.dy);
      sctx.drawImage(shade, L.sx, 0, L.sw, up, 0, L.dy - up * k, L.dw, up * k);
    }
    if (down) {
      sctx.fillStyle = average(src, L.sx, L.sw, H - 2);
      sctx.fillRect(0, below, cw, ch - below);
      sctx.drawImage(shade, L.sx, up, L.sw, down, 0, below, L.dw, down * k);
    }
  }

  function draw(v, L, alpha) {
    sctx.globalAlpha = alpha;
    if (L.band) surround(v.buf, L);
    sctx.drawImage(v.buf, L.sx, L.sy, L.sw, L.sh, L.dx, L.dy, L.dw, L.dh);
    sctx.globalAlpha = 1;
  }

  /** draw view a, optionally cross-fading to view b by `mix` (0..1); false when nothing could be drawn (no buffer,
   *  or the graphics context is lost) */
  function present(a, b = null, mix = 0, focusB = W / 2, legacyMix = 0) {
    // Old cached apps call present(canvas, focusX, canvasB, focusB, mix). Keep them working during migration
    // to the production bundle, whose display and app always come from the same release.
    if (typeof b === 'number') {
      const legacy = (buf, focusX) => ({ buf, scene: { focusX, landmark: [focusX, focusX] } });
      a = legacy(a, b);
      b = mix ? legacy(mix, focusB) : null;
      mix = legacyMix;
    }
    if (!a?.buf || lost) return false;
    sctx.imageSmoothingEnabled = false;
    const L = place(a);
    if (L.bars) {
      const back = b && mix > 0.5 ? b.buf : a.buf;
      sctx.drawImage(back, 0, 0, W, H, 0, 0, cw, ch);
      sctx.fillStyle = 'rgba(4,4,12,0.78)';
      sctx.fillRect(0, 0, cw, ch);
    }
    draw(a, L, 1);
    if (b && mix > 0) draw(b, place(b), mix);
    return true;
  }

  /** the page's own dark instead of the last frame: for a scene that stopped being drawn (site/js/app.js sceneFailed) */
  function blank() {
    sctx.fillStyle = '#05060f';
    sctx.fillRect(0, 0, cw, ch);
  }

  /** where the scene rect r ({ x, y, w, h }) of view v is on the page, in CSS px (clipped by a portrait crop), or null when most of it is cropped off */
  function spot(v, r) {
    if (typeof v === 'number') v = { scene: { focusX: v, landmark: [v, v] } }; // old spot(focusX, rect)
    if (!v || !r) return null;
    const L = place(v), k = L.dw / L.sw, css = innerWidth / cw;
    const x0 = Math.max(L.dx, L.dx + (r.x - L.sx) * k), x1 = Math.min(L.dx + L.dw, L.dx + (r.x + r.w - L.sx) * k);
    if (x1 - x0 < r.w * k * 0.6) return null;
    const y0 = L.dy + (r.y - L.sy) * k;
    return { left: x0 * css, top: y0 * css, width: (x1 - x0) * css, height: r.h * k * css };
  }

  /** drag portrait view v by dx CSS px (+ = the finger moves right); returns the part it couldn't follow, past the scene's end */
  function pan(v, dx) {
    if (cw >= ch) return dx;                          // landscape shows (nearly) the whole scene: no drag
    const L = place(v), k = innerWidth / L.sw;        // CSS px per scene px
    const off = clamp(L.home + (pans.get(v) ?? 0), 0, W - L.sw) - L.home;
    const next = clamp(L.home + off - dx / k, 0, W - L.sw) - L.home;
    pans.set(v, next);
    if (next !== off) panned = true;
    return dx + (next - off) * k;
  }

  /** back to v's frame (a city being switched to starts there) */
  const recentre = v => pans.delete(v);

  /** after present(): a transparent 480x270 overlay (lanterns, reactions) in the same place as view v's scene, pan
   *  included, so it stays pixel-crisp and lines up in every crop; `op` 'lighter' adds a glow layer */
  function overlay(v, buf, op = 'source-over') {
    const L = place(v);
    sctx.imageSmoothingEnabled = false;
    sctx.globalCompositeOperation = op;
    sctx.drawImage(buf, L.sx, L.sy, L.sw, L.sh, L.dx, L.dy, L.dw, L.dh);
    sctx.globalCompositeOperation = 'source-over';
  }

  /**
   * after present(): something small that crosses view v's scene smoothly, at the screen's frame rate. Its origin is
   * the scene point (x, y), fractions of a scene pixel included, snapped to the screen's own pixels; `draw(put)` then
   * puts its pieces: put(img, sx, sy, sw, sh, ox, oy, op = 'source-over') draws img's rect ox, oy scene pixels from the
   * origin, scaled like the scene. Pieces at whole offsets keep their size and their place among each other whatever
   * the fraction, so the thing moves as one; a fraction in oy slides a piece smoothly up or down. Clipped to the
   * scene's box.
   */
  function sprites(v, x, y, draw) {
    const L = place(v), k = L.dw / L.sw;
    const x0 = Math.round(L.dx + (x - L.sx) * k), y0 = Math.round(L.dy + (y - L.sy) * k);
    sctx.save();
    sctx.beginPath();
    sctx.rect(L.dx, L.dy, L.dw, L.dh);
    sctx.clip();
    sctx.imageSmoothingEnabled = false;
    draw((img, sx, sy, sw, sh, ox, oy, op = 'source-over') => {
      const left = Math.round(ox * k);
      sctx.globalCompositeOperation = op;
      sctx.drawImage(img, sx, sy, sw, sh, x0 + left, y0 + Math.round(oy * k), Math.round((ox + sw) * k) - left, Math.round(sh * k));
    });
    sctx.restore();
  }

  /** what of view v's scene is on screen: columns [x, x + w) and rows [y, y + h), shown in the CSS-pixel box
   *  { left, top, width, height } (to place things inside the crop, or above the interface) */
  function view(v) {
    const L = place(v), kx = innerWidth / cw, ky = innerHeight / ch;
    return { x: L.sx, y: L.sy, w: L.sw, h: L.sh, left: L.dx * kx, top: L.dy * ky, width: L.dw * kx, height: L.dh * ky };
  }

  return { resize, present, blank, spot, pan, recentre, overlay, sprites, view, due, frameBillboard };
}
